import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Download, Inbox, Building2, BarChart3, Trash2, Plus } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const TABS = [
  { id: "leads", label: "Demandes", icon: Inbox },
  { id: "companies", label: "Sociétés", icon: Building2 },
  { id: "stats", label: "Chiffres clés", icon: BarChart3 },
];

const STATUSES = ["nouveau", "en cours", "qualifié", "clôturé"];
const fieldCls = "w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white outline-none focus:border-[var(--gold)]/60";

function Leads() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  const download = async (path, name) => {
    try {
      const token = localStorage.getItem("losta_token");
      const res = await fetch(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || "dossier"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  if (!leads.length) return <p className="text-slate-500 py-10 text-center">Aucune demande pour l'instant.</p>;

  return (
    <div className="space-y-4" data-testid="admin-leads">
      {leads.map((l) => (
        <div key={l.id} className="rounded-xl border border-white/10 card-lux p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-serif text-xl text-white">{l.prenom} {l.nom} · <span className="text-gold">{l.entreprise}</span></div>
              <div className="text-sm text-slate-400 mt-1">{l.email} · {l.telephone} · {l.pays} {l.region && `(${l.region})`}</div>
              <div className="text-xs text-slate-500 mt-1">{l.secteur} · {l.type_projet} · {l.salaries} salariés · CA {l.chiffre_affaires || "—"}</div>
            </div>
            <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className={`${fieldCls} !w-auto`} data-testid={`lead-status-${l.id}`}>
              {STATUSES.map((s) => <option key={s} className="bg-[#0F1118]">{s}</option>)}
            </select>
          </div>
          {l.message && <p className="mt-3 text-sm text-slate-300 bg-white/[0.02] rounded-lg p-3">{l.message}</p>}
          {l.document_path && (
            <button onClick={() => download(l.document_path, l.document_name)} className="mt-3 inline-flex items-center gap-2 text-sm text-gold" data-testid={`lead-download-${l.id}`}>
              <Download className="h-4 w-4" /> {l.document_name || "Dossier joint"}
            </button>
          )}
          <div className="text-xs text-slate-600 mt-3">{new Date(l.created_at).toLocaleString("fr-FR")}</div>
        </div>
      ))}
    </div>
  );
}

const emptyCompany = { name: "", city: "", country: "France", pole: "", metier: "", description: "", website: "", photo_url: "", year: "", kind: "acquisition", is_published: true };

function Companies() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyCompany);
  const load = () => api.get("/admin/companies").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.metier) { toast.error("Nom, ville et métier requis"); return; }
    await api.post("/admin/companies", form);
    toast.success("Société ajoutée");
    setForm(emptyCompany); load();
  };
  const del = async (id) => { await api.delete(`/admin/companies/${id}`); toast.success("Supprimée"); load(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]" data-testid="admin-companies">
      <form onSubmit={add} className="rounded-xl border border-white/10 card-lux p-5 h-fit">
        <div className="eyebrow mb-4">Ajouter une société / acquisition</div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Nom" className={fieldCls} value={form.name} onChange={set("name")} data-testid="company-name" />
          <input placeholder="Ville" className={fieldCls} value={form.city} onChange={set("city")} />
          <select className={fieldCls} value={form.country} onChange={set("country")}>
            <option className="bg-[#0F1118]">France</option><option className="bg-[#0F1118]">Suisse</option>
          </select>
          <input placeholder="Métier" className={fieldCls} value={form.metier} onChange={set("metier")} />
          <input placeholder="Pôle (ex: Losta Tech SAS)" className={fieldCls} value={form.pole} onChange={set("pole")} />
          <input placeholder="Année" className={fieldCls} value={form.year} onChange={set("year")} />
          <input placeholder="Site web" className={`${fieldCls} col-span-2`} value={form.website} onChange={set("website")} />
          <input placeholder="URL photo" className={`${fieldCls} col-span-2`} value={form.photo_url} onChange={set("photo_url")} />
          <select className={`${fieldCls} col-span-2`} value={form.kind} onChange={set("kind")}>
            <option className="bg-[#0F1118]" value="acquisition">Acquisition (a rejoint le groupe)</option>
            <option className="bg-[#0F1118]" value="marque">Marque du groupe</option>
          </select>
          <textarea placeholder="Description" rows={3} className={`${fieldCls} col-span-2`} value={form.description} onChange={set("description")} />
        </div>
        <button type="submit" className="btn-gold mt-4 w-full rounded-full px-5 py-2.5 text-xs uppercase tracking-wider flex items-center justify-center gap-2" data-testid="company-add">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </form>

      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-white/10 card-lux p-4">
            <div>
              <div className="text-white font-medium">{c.name} <span className="text-xs text-slate-500">· {c.kind}</span></div>
              <div className="text-xs text-slate-400">{c.city} · {c.country} · {c.metier}</div>
            </div>
            <button onClick={() => del(c.id)} className="text-slate-500 hover:text-red-400" data-testid={`company-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsEditor() {
  const [form, setForm] = useState({ entreprises: "", collaborateurs: "", implantations: "", pays: "2" });
  useEffect(() => { api.get("/stats").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => { e.preventDefault(); await api.put("/admin/stats", form); toast.success("Chiffres mis à jour"); };
  return (
    <form onSubmit={save} className="max-w-md rounded-xl border border-white/10 card-lux p-6" data-testid="admin-stats">
      <div className="eyebrow mb-4">Chiffres clés affichés publiquement</div>
      <p className="text-xs text-slate-500 mb-4">Laissez vide pour afficher « — ». Ne renseignez que des données réelles.</p>
      {[["entreprises", "Entreprises"], ["collaborateurs", "Collaborateurs"], ["implantations", "Implantations"], ["pays", "Pays"]].map(([k, label]) => (
        <label key={k} className="block mb-3">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-slate-400">{label}</span>
          <input className={fieldCls} value={form[k] || ""} onChange={set(k)} data-testid={`stat-input-${k}`} />
        </label>
      ))}
      <button type="submit" className="btn-gold mt-3 rounded-full px-6 py-2.5 text-xs uppercase tracking-wider" data-testid="stats-save">Enregistrer</button>
    </form>
  );
}

export default function AdminDashboard() {
  useSeo({ title: "Administration | Losta Group", description: "Espace réservé.", path: "/admin" });
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);
  if (!user) return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#07080B]">
      <header className="border-b border-white/10 backdrop-blur-xl bg-[#07080B]/80 sticky top-0 z-40">
        <div className="mx-auto max-w-[1200px] px-5 h-18 py-4 flex items-center justify-between">
          <Logo />
          <button onClick={() => { logout(); nav("/admin/login"); }} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-5 py-10">
        <h1 className="font-serif text-4xl text-white mb-8">Espace confidentiel</h1>
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[var(--gold)] text-gold" : "border-transparent text-slate-400 hover:text-white"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "leads" && <Leads />}
        {tab === "companies" && <Companies />}
        {tab === "stats" && <StatsEditor />}
      </div>
    </div>
  );
}
